import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OrderDatePicker } from "@/components/admin/OrderDatePicker";
import { DeleteOrderButton } from "@/components/admin/DeleteOrderButton";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PlatterDetails } from "@/components/PlatterDetails";

const STATUSES = [
  { key: "pending", label: "New" },
  { key: "preparing", label: "In progress" },
  { key: "ready", label: "Ready" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Canceled" },
];

const DATE_FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_week", label: "Last week" },
  { key: "all", label: "All orders" },
];

const ORDER_TIME_ZONE = "America/New_York";

const orderDateParts = (timestamp) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ORDER_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(timestamp));
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
};

const dateKey = (timestamp) => {
  const { year, month, day } = orderDateParts(timestamp);
  return `${year}-${month}-${day}`;
};

const easternDate = (timestamp) => {
  const { year, month, day } = orderDateParts(timestamp);
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
};

const groupLabel = (timestamp) => {
  const date = easternDate(timestamp);
  const today = easternDate(new Date());
  const difference = Math.round((today - date) / 86400000);
  const todayWeekday = today.getUTCDay() || 7;
  const thisWeekStart = new Date(today);
  thisWeekStart.setUTCDate(today.getUTCDate() - todayWeekday + 1);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setUTCDate(thisWeekStart.getUTCDate() - 7);
  if (difference === 0) return "Today";
  if (difference === 1) return "Yesterday";
  if (date >= thisWeekStart) {
    return `This week · ${date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })}`;
  }
  if (date >= lastWeekStart) {
    return `Last week · ${date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })}`;
  }
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatTime = (timestamp) => {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: ORDER_TIME_ZONE,
  });
};

export default function Orders() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [dateFilter, setDateFilter] = useState("today");
  const [selectedDate, setSelectedDate] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams({ date_filter: dateFilter });
    if (sid) params.set("store_id", sid);
    if (dateFilter === "custom" && selectedDate) params.set("date", selectedDate);
    const response = await api.get(`/orders?${params.toString()}`);
    setOrders(response.data);
  }, [dateFilter, selectedDate, sid]);

  useEffect(() => {
    load().catch(() => setOrders([]));
  }, [load]);

  const groupedOrders = useMemo(() => {
    const groups = [];
    const groupMap = new Map();
    orders.forEach((order) => {
      const key = dateKey(order.created_at);
      if (!groupMap.has(key)) {
        const group = { key, label: groupLabel(order.created_at), orders: [] };
        groupMap.set(key, group);
        groups.push(group);
      }
      groupMap.get(key).orders.push(order);
    });
    return groups;
  }, [orders]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const chooseQuickFilter = (filter) => {
    setDateFilter(filter);
    setSelectedDate("");
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="eyebrow mb-2">Operations</p>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="admin-orders-title">
            Orders
          </h1>
          <p className="mt-2 text-sm text-neutral-500" data-testid="orders-date-context">
            {dateFilter === "custom" && selectedDate
              ? `Orders from ${new Date(`${selectedDate}T00:00:00`).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}`
              : "Review orders by the day or search a specific calendar date."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex flex-wrap rounded-full border border-neutral-200 bg-white p-1">
            {DATE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => chooseQuickFilter(filter.key)}
                className={`h-8 rounded-full px-3 text-xs font-medium transition-colors ${
                  dateFilter === filter.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
                }`}
                data-testid={`orders-filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <OrderDatePicker
            value={selectedDate}
            onSelectDate={(date) => {
              setSelectedDate(date);
              setDateFilter("custom");
            }}
            onClear={() => chooseQuickFilter("today")}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-neutral-200/70 bg-white">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Order #</th>
              <th className="p-4">Received at (ET)</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Items</th>
              <th className="p-4">Fulfillment</th>
              <th className="p-4">Total</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {groupedOrders.map((group) => (
              <OrderDayGroup
                key={group.key}
                group={group}
                onStatusChange={setStatus}
                onOrderDeleted={load}
                user={user}
              />
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-neutral-500" data-testid="orders-empty-state">
                  No orders in this date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderDayGroup({ group, onStatusChange, onOrderDeleted, user }) {
  return (
    <>
      <tr className="bg-neutral-50/80" data-testid={`orders-day-group-${group.key}`}>
        <td colSpan={8} className="px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-600">
            <CalendarDays className="h-3.5 w-3.5" />
            {group.label}
            <span className="normal-case tracking-normal text-neutral-400">
              {group.orders.length} {group.orders.length === 1 ? "order" : "orders"}
            </span>
          </div>
        </td>
      </tr>
      {group.orders.map((order) => (
        <tr key={order.id} data-testid={`admin-order-row-${order.id}`}>
          <td className="p-4 font-mono text-xs">{order.order_number}</td>
          <td className="p-4">
            <span className="font-medium" data-testid={`order-timestamp-${order.id}`}>
              {formatTime(order.created_at)}
            </span>
          </td>
          <td className="p-4">
            <div className="font-medium">{order.customer_name}</div>
            <div className="text-xs text-neutral-500">{order.customer_phone || "—"}</div>
          </td>
          <td className="max-w-xs p-4 text-neutral-600" title={order.items.map((item) => item.name).join(", ")}>
            {order.items.map((item) => (
              <div key={item.cart_line_id || item.item_id} className="mb-2 last:mb-0">
                <p>{item.quantity}× {item.name}</p>
                <PlatterDetails platter={item.platter} compact />
              </div>
            ))}
          </td>
          <td className="p-4 capitalize">
            {order.fulfillment}
            <span className="text-xs text-neutral-400"> · {order.location || "—"}</span>
            {order.pickup_date && <div className="text-xs text-neutral-500">{order.pickup_date}</div>}
          </td>
          <td className="p-4 font-semibold">${order.total.toFixed(2)}</td>
          <td className="p-4">
            <Select value={order.status} onValueChange={(value) => onStatusChange(order.id, value)}>
              <SelectTrigger className="h-9 w-36" data-testid={`status-select-${order.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((status) => (
                  <SelectItem key={status.key} value={status.key}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </td>
          <td className="p-4 text-right">
            <DeleteOrderButton order={order} onDeleted={onOrderDeleted} user={user} />
          </td>
        </tr>
      ))}
    </>
  );
}